import React, { useState, useEffect, useCallback } from 'react';
import { useEditorContext } from '@/scenes/engine';
import { useSelector } from 'react-redux';
import { selectImageElements } from '@/store/slices/imageElement/selectors';
import { selectUser, selectUserIsPremium } from '@/store/slices/user/selectors';
import useAppContext from '@/hooks/useAppContext';
import { ActionItem } from './ActionItem';
import { FaceSwapDataObject } from './faceSwapStaticData';
import ActionMenu from './ActionMenu';
import useDraggable from './hook/useDraggable';
import TooltipConfirm from './TooltipConfirm';
import { customAmplitude } from '@/utils/customAmplitude';
import { createPortal } from 'react-dom';
import { StaticImage } from 'fabric/fabric-impl';
import { CSSProperties } from 'react';
import { Z_INDEX } from '@/constants/z-index';
import { useAppDispatch } from '@/store/store';
import { setOpenModalTryPremium } from '@/store/slices/user/actions';
import IntroEditAI from '@assets/video/IntroEditAI.mp4'
import VideoIntroAIImagePlaceHolder from '@assets/images/EWAPlaceholder.jpg'

type ActionItemMap = {
    [key: string]: ActionItem[];
};

export interface ImageProcessorProps {
    style?: CSSProperties;
    hasInput?: boolean;
    hasQuickActions?: boolean;
    hasHeader?: boolean;
    hasShowMoreBtn?: boolean
    title?: string
    activeObject: StaticImage;
    handleClose?: () => void;
    skipEditorIntegration?: boolean;
    onStartLoading?: (prompt?: string) => void
    onStopLoading?: () => void
    onSuccess?: () => void
    wrapMenuStyle?: CSSProperties
    hasScroll?: boolean
    onActionSelect?: (actionItem) => void
    suggestionType?: 'edit' | 'background'
    directImage?: string // Base64 image to use directly instead of extracting from activeObject
}

const ImageProcessor: React.FC<ImageProcessorProps> = ({
  style,
  hasInput = true,
  hasQuickActions = true,
  hasHeader = true,
  hasShowMoreBtn = true,
  title,
  activeObject,
  handleClose,
  skipEditorIntegration = false,
  onStartLoading,
  onStopLoading,
  onSuccess,
  wrapMenuStyle,
  hasScroll,
  onActionSelect,
  suggestionType = 'edit',
  directImage,
}) => {
    const { editor } = useEditorContext()
    const {
        isOpenTutorial,
        isOpenPixelManipulationObject,
    } = useAppContext()
    
    const hasPremium = useSelector(selectUserIsPremium)
    const user = useSelector(selectUser)
    const { ref, onMouseDown, dragging } = useDraggable();

    // Local state for UI
    const [loadingActions, setLoadingActions] = useState(true);
    const [showTooltip, setShowTooltip] = useState(false)

    // Local UI loading states (decoupled from handler states)
    const [genImageLoadingUI, setGenImageLoadingUI] = useState(false)
    const [isLoadingFromTextUI, setIsLoadingFromTextUI] = useState(false)
    const [genImageErrorUI, setGenImageErrorUI] = useState('')
    
    // Local state for directImage suggestions (not cached in handler)
    const [directSuggestions, setDirectSuggestions] = useState<ActionItem[]>([])

    // Get states from the handler
    const handlerStates = editor?.handlers?.editWithAiHandler?.getLoadingStates() || {
        genImageLoading: false,
        loadingActions: false,
        isLoadingFromText: false,
        showTooltip: false,
    }

    const { 
    } = handlerStates

    const dispatch = useAppDispatch()

    // Set up error change callback
    useEffect(() => {
        if (editor?.handlers?.editWithAiHandler) {
            editor.handlers.editWithAiHandler.onErrorChange = (error: string) => {
                setGenImageErrorUI(error)
            }
        }
        return () => {
            if (editor?.handlers?.editWithAiHandler) {
                editor.handlers.editWithAiHandler.onErrorChange = undefined
            }
        }
    }, [editor?.handlers?.editWithAiHandler])

    // Helper function to get actions for the current object
    const getActionsForCurrentId = useCallback((filterPlaceholder: boolean): ActionItem[] => {
        if (!editor?.handlers?.editWithAiHandler || !activeObject) {
            return []
        }
        return editor.handlers.editWithAiHandler.getActionsForObject(activeObject, filterPlaceholder)
    }, [editor?.handlers?.editWithAiHandler, activeObject])

    // Helper function to check if current object has faces
    const getHasFaceForCurrentId = (): boolean => {
        if (!editor?.handlers?.editWithAiHandler || !activeObject) {
            return false
        }
        return editor.handlers.editWithAiHandler.getHasFaceForObject(activeObject)
    }

    // Load suggestions when activeObject changes
    useEffect(() => {
        if (!editor?.handlers?.editWithAiHandler || !activeObject) {
            setLoadingActions(false)
            return
        }

        if (!user) {
            setDirectSuggestions([])
            setLoadingActions(false)
            return
        }

        if (isOpenTutorial && !skipEditorIntegration) {
            setLoadingActions(false)
            return
        }

        const loadSuggestions = async () => {
            setLoadingActions(true)
            try {
                const suggestions = await editor.handlers.editWithAiHandler.suggestEdits(
                    activeObject, 
                    suggestionType,
                    directImage
                )
                // If using directImage, store in local state (not cached in handler)
                if (directImage && suggestions) {
                    setDirectSuggestions(suggestions)
                }
            } catch (error) {
                console.error('Failed to load suggestions:', error)
            } finally {
                setLoadingActions(false)
            }
        }

        // Check if we already have actions (skip for directImage - no caching)
        if (!directImage) {
            const existingActions = getActionsForCurrentId(true)
            if (existingActions.length > 0) {
                setLoadingActions(false)
                return
            }
        } else if (directSuggestions.length > 0) {
            // For directImage, check local state
            setLoadingActions(false)
            return
        }

        loadSuggestions()
    }, [
        activeObject,
        hasPremium,
        editor?.handlers?.editWithAiHandler,
        isOpenTutorial,
        skipEditorIntegration,
        suggestionType,
        directImage,
        user
    ])

    // Handle action execution
    const runAction = async (actionItem: ActionItem, hasFace: boolean, callback?: () => void) => {
        if(onActionSelect) {
            onActionSelect(actionItem)
            return
        }
        if (!editor?.handlers?.editWithAiHandler || !activeObject) {
            return
        }

        const loadingAction = async () => {
            try {
                handleClose()
                setGenImageLoadingUI(true)
                setIsLoadingFromTextUI(false)
                onStartLoading?.(actionItem.prompt)
                const success = await editor.handlers.editWithAiHandler.runAction(
                    activeObject,
                    actionItem,
                    hasFace,
                    isOpenPixelManipulationObject,
                    callback
                )
                if(success) {
                    onSuccess?.()
                }
            } finally {
                setGenImageLoadingUI(false)
                setIsLoadingFromTextUI(false)
                onStopLoading?.()
            }
        }

        const baseModal = {
            source: 'BtSubscriptionAIEdit',
            callback: async() => {
                await loadingAction()
            },
        };

        if(!hasPremium) {
            dispatch(setOpenModalTryPremium({
                ...baseModal,
                isOpen: true,
                opened: true,
                video: IntroEditAI,
                placeholderImage: VideoIntroAIImagePlaceHolder,
            }))
            customAmplitude('Premium Prompt', { Source: 'BtSubscriptionAIEdit', Type: 'Standard' })
            handleClose()
            return
        }

        loadingAction()
    }

    // Handle custom prompt submission
    const runSubmit = async (inputValue: string, hasFace: boolean) => {
        if (!editor?.handlers?.editWithAiHandler || !activeObject) {
            return
        }

        try {
            setGenImageLoadingUI(true)
            setIsLoadingFromTextUI(true)
            onStartLoading?.()
            await editor.handlers.editWithAiHandler.runSubmit(
                activeObject,
                inputValue,
                hasFace,
                isOpenPixelManipulationObject
            )
        } finally {
            setGenImageLoadingUI(false)
            setIsLoadingFromTextUI(false)
            onStopLoading?.()
        }
    }

    // Handle loading more actions
    const loadMoreActions = async () => {
        if (!editor?.handlers?.editWithAiHandler || !activeObject) {
            return
        }
        setLoadingActions(true)
        try {
            const newActions = await editor.handlers.editWithAiHandler.loadMoreActions(
                activeObject,
                isOpenPixelManipulationObject,
                suggestionType,
                directImage
            )
            // If using directImage, store in local state (not cached in handler)
            if (directImage && newActions) {
                setDirectSuggestions(newActions)
            }
        } finally {
            setLoadingActions(false)
        }
    }

    // Handle sign in
    const handleSignIn = () => {
        const eventProperties = {
            Source: 'BtSubscriptionAIEdit',
            Type: 'Standard',
        }
        customAmplitude('Premium Prompt', eventProperties)
        // Additional sign in logic can be added here if needed
    }

    // Handle closing panel
    const closePanel = () => {
        if (handleClose) {
            handleClose()
        }
    }

    // Clear error
    const handleClearError = () => {
        if (editor?.handlers?.editWithAiHandler) {
            editor.handlers.editWithAiHandler.clearError()
        } else {
            setGenImageErrorUI('')
        }
    }

    if ((isOpenTutorial) && !skipEditorIntegration) {
        return null;
    }

    return (
        <div
            ref={ref}
            onMouseDown={onMouseDown}
            onDragOver={(e) => e.preventDefault()}
            style={{
                zIndex: Z_INDEX.IMAGE_PROCESSOR,
                display: isOpenPixelManipulationObject ? 'none' : 'block',
                ...style
            }}
            id="action-menu"
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <ActionMenu
                    actions={directImage ? directSuggestions : getActionsForCurrentId(true)}
                    onActionSelect={runAction}
                    loading={loadingActions}
                    onSubmit={runSubmit}
                    hasFaces={getHasFaceForCurrentId()}
                    hasPremium={hasPremium}
                    isLoggedIn={!!user}
                    onShowMore={loadMoreActions}
                    hasShowMoreBtn={hasShowMoreBtn}
                    onClose={closePanel}
                    genImageLoading={genImageLoadingUI}
                    genImageError={genImageErrorUI}
                    onClearError={handleClearError}
                    onSignIn={handleSignIn}
                    isLoadingFromText={isLoadingFromTextUI}
                    hasInput={hasInput}
                    hasQuickActions={hasQuickActions}
                    hasHeader={hasHeader}
                    title={title}
                    wrapMenuStyle={wrapMenuStyle}
                    hasScroll={hasScroll}
                />
            </div>
            
            {showTooltip
          ? createPortal(
              <TooltipConfirm setShowTooltip={setShowTooltip}  />,
              document.body
            )
          : null}
        </div>
    );
};

export default ImageProcessor